import apiClient from './client';

export type CostBasisMethod = 'fifo' | 'lifo' | 'hifo';

export interface TaxLotView {
  lotId: number;
  symbol: string;
  amount: number;
  costBasisPerUnit: number;
  totalCostBasis: number;
  currentPriceUsd: number | null;
  currentValueUsd: number | null;
  unrealisedGainLoss: number | null;
  unrealisedGainLossPct: number | null;
  dateAcquired: string;
  holdingPeriod: 'SHORT_TERM' | 'LONG_TERM';
  holdingDays: number;
}

export interface TaxLotsSummary {
  totalCostBasis: number;
  totalCurrentValue: number | null;
  totalUnrealisedGainLoss: number | null;
  lotCount: number;
}

export interface TaxLotsResponse {
  userId: string;
  method: CostBasisMethod;
  symbol: string;
  lots: TaxLotView[];
  summary: TaxLotsSummary;
}

export async function getTaxLots(
  userId: string,
  method: CostBasisMethod = 'fifo',
  symbol?: string,
): Promise<TaxLotsResponse> {
  const params: Record<string, string> = { method };
  if (symbol) params.symbol = symbol;
  const { data } = await apiClient.get<TaxLotsResponse>(
    `/analytics/portfolio/tax-lots/${userId}`,
    { params },
  );
  return data;
}

export async function updateCostBasisMethod(method: CostBasisMethod): Promise<void> {
  await apiClient.patch('/auth/profile', { costBasisMethod: method });
}

// ─── Tax Harvest Opportunities (Chunk 79) ─────────────────────────────────────

export interface HarvestOpportunity {
  lotId: number;
  userId: string;
  symbol: string;
  amount: number;
  costBasisPerUnit: number;
  totalCostBasis: number;
  currentPriceUsd: number;
  currentValueUsd: number;
  unrealisedLossUsd: number;
  holdingDays: number;
  dateAcquired: string;
  washSaleSafe: boolean;
}

export interface HarvestOpportunitiesResponse {
  userId: string;
  threshold: number;
  opportunities: HarvestOpportunity[];
  totalUnrealisedLoss: number;
  count: number;
}

export async function getHarvestOpportunities(
  userId: string,
  threshold = 0,
  minDays = 30,
): Promise<HarvestOpportunitiesResponse> {
  const { data } = await apiClient.get<HarvestOpportunitiesResponse>(
    `/analytics/tax/harvest-opportunities/${userId}`,
    { params: { threshold: String(threshold), minDays: String(minDays) } },
  );
  return data;
}

export async function acknowledgeHarvestOpportunity(
  userId: string,
  lotId: number,
): Promise<void> {
  await apiClient.post(
    `/analytics/tax/harvest-opportunities/${userId}/${lotId}/acknowledge`,
  );
}

